import {useEffect, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import strUtil from 'util/strUtil';

import type {EntryTemplateForConfigureButton} from './ConfigureExecutionTemplateButton';
import type {MinimalProcessTaskSchema} from './ProcessTaskSchemaUtils';

export interface StatusLifecycleStateArgs {
  processTaskSchema: MinimalProcessTaskSchema | null;
  statusLifecycles: Array<{id: string; name: string; schemaExecutionType: ProcessExecutionType}> | null;
  executionType: ProcessExecutionType;
  setExecutionTemplate: React.Dispatch<React.SetStateAction<EntryTemplateForConfigureButton>>;
  setDefaultEntryExecutionFolderId: React.Dispatch<React.SetStateAction<string | null>>;
}

export interface StatusLifecycleState {
  statusLifecycle: {id: string; name: string} | undefined;
  statusLifecyclesForExecutionType: Array<{
    id: string;
    name: string;
    schemaExecutionType: ProcessExecutionType;
  }>;
  setStatusLifecycle: React.Dispatch<React.SetStateAction<{id: string; name: string} | undefined>>;
}

export function useStatusLifecycleState(args: StatusLifecycleStateArgs): StatusLifecycleState {
  const {
    processTaskSchema,
    statusLifecycles,
    executionType,
    setExecutionTemplate,
    setDefaultEntryExecutionFolderId,
  } = args;

  const statusLifecyclesForExecutionType = useMemo(() => {
    const lifecycles = (statusLifecycles ?? [])
      .filter(({schemaExecutionType}) => schemaExecutionType === executionType)
      .map((statusLifecycle) => ({...statusLifecycle, name: strUtil.toSentenceCase(statusLifecycle.name)}));
    assert(
      statusLifecycles == null || lifecycles.length > 0,
      `Expected at least one status lifecycle for executionType ${executionType}`
    );
    return lifecycles;
  }, [statusLifecycles, executionType]);

  const [statusLifecycle, setStatusLifecycle] = useState<{id: string; name: string} | undefined>(
    processTaskSchema?.statusLifecycle ?? statusLifecyclesForExecutionType[0]
  );

  useEffect(() => {
    setStatusLifecycle(processTaskSchema?.statusLifecycle ?? statusLifecyclesForExecutionType[0]);
  }, [statusLifecyclesForExecutionType, processTaskSchema]);

  useEffect(() => {
    if (processTaskSchema?.statusLifecycle == null) {
      setStatusLifecycle(statusLifecyclesForExecutionType[0]);
    }
    if (executionType !== ProcessExecutionType.ENTRY) {
      setExecutionTemplate(null);
      setDefaultEntryExecutionFolderId(null);
    }
    // statusLifecyclesForExecutionType is updated when executionType updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionType]);

  return {
    statusLifecycle,
    statusLifecyclesForExecutionType,
    setStatusLifecycle,
  };
}
